import { createTRPCClient, httpBatchLink, httpLink, isNonJsonSerializable, splitLink } from '@trpc/client'
import type { TRPCRouter } from '@klear/trpc-types'
import { HEADER_KLEAR_VERSION } from '@klear/constants'
import { requestBuildVersionCheck } from '@klear/ui/hooks/use-build-version-gate'
// eslint-disable-next-line klear-browser-wire/no-browser-typebox-wire-imports
import { klearTransformer } from '@klear/registry/trpc'
import isArray from 'lodash/isArray'
import assign from 'lodash/assign'
import isFunction from 'lodash/isFunction'
import isObject from 'lodash/isObject'
import noop from 'lodash/noop'

function inputHasFiles(input: unknown): boolean {
  const files = input && isObject(input) && 'files' in input ? (input as { files?: unknown }).files : undefined
  return isArray(files) && files.some((f: unknown) => f instanceof File)
}

function getFetchPreconnectStatic(): (
  url: string | URL,
  options?: { dns?: boolean; tcp?: boolean; http?: boolean; https?: boolean }
) => void {
  const root = globalThis.fetch as typeof globalThis.fetch & {
    preconnect?: (
      url: string | URL,
      options?: { dns?: boolean; tcp?: boolean; http?: boolean; https?: boolean }
    ) => void
  }
  if (isFunction(root.preconnect)) {
    return root.preconnect.bind(globalThis.fetch)
  }
  return noop
}

function asTrpcFetch(fn: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>): typeof fetch {
  return assign(fn, { preconnect: getFetchPreconnectStatic() }) as typeof fetch
}

let lastServerVersion = ''

export function noteServerVersion(version: string | null): void {
  if (!version || version === lastServerVersion) return
  lastServerVersion = version
  requestBuildVersionCheck()
}

function withServerVersionCheck(
  fn: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return async (input, init) => {
    const response = await fn(input, init)
    noteServerVersion(response.headers.get(HEADER_KLEAR_VERSION))
    return response
  }
}

function defaultKlearFetch(url: RequestInfo | URL, options?: RequestInit) {
  return fetch(url, {
    ...options,
    credentials: 'include'
  })
}

export type CreateKlearTrpcClientOptions = {
  apiUrl?: string
  unbatchMutations?: boolean
  fetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
}

export function createKlearTrpcClient(options?: CreateKlearTrpcClientOptions) {
  const apiUrl = options?.apiUrl ?? '/api'
  const unbatchMutations = options?.unbatchMutations ?? true
  const rawFetch = options?.fetch ?? defaultKlearFetch
  const customFetch = asTrpcFetch(withServerVersionCheck(rawFetch))

  return createTRPCClient<TRPCRouter>({
    links: [
      splitLink({
        condition: (op) => {
          const hasFiles = inputHasFiles(op.input)
          const nonJson = isNonJsonSerializable(op.input)
          if (unbatchMutations) {
            return op.type === 'mutation' || nonJson || hasFiles
          }
          return nonJson || hasFiles
        },
        true: httpLink({
          transformer: klearTransformer,
          url: apiUrl,
          fetch: customFetch
        }),
        false: httpBatchLink({
          transformer: klearTransformer,
          url: apiUrl,
          fetch: customFetch
        })
      })
    ]
  })
}
